import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';

import {
  createColumnHelper,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  OnChangeFn,
  PaginationState,
  RowSelectionState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';

import { ColumnConfigPopover } from './column-config-popover';
import { FilterPopover } from './filter-popover';
import formatDate from '../../../../../../../helpers/formatDate';
import { ServerFilters } from '../../components/subset-form';
import { MatterDocFilterOptions, MatterDocsFilters } from '@/../../backend/src/types/types';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { FilterField } from '@/api/queries/useGetMetadataFilterOptions';
import { DocWithFacts } from '@/api/types/queries';
import { useColumnConfiguration } from '@/hooks/useColumnConfiguration';
import { useDocumentNaturalLanguageFilters } from '@/hooks/useNaturalLanguageFilters';
import { Chip } from '@/new/components/chip';
import { FilterPills } from '@/new/components/filter-pills';
import { Icon } from '@/new/components/icon';
import { NaturalLanguageSearchBox } from '@/new/components/natural-language-search-bar';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/new/components/shadcn/empty';
import { Popover, PopoverTrigger, PopoverContent } from '@/new/components/shadcn/popover';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/new/components/shadcn/table';
import { Toggle } from '@/new/components/shadcn/toggle';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { SortIcon } from '@/new/components/table/sort-icon';
import { TableNullField } from '@/new/components/table/table-null-field';
import { TablePagination } from '@/new/components/table/table-pagination';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';
import { arrInArr, includedInArr, selectedRowsFilter, sortStringWithEmptyLast } from '@/new/lib/table-utils';
import { cn } from '@/new/lib/utils';
import { useStore } from '@/store';
import { DocFilters } from '@/types';
import { ATTRIBUTE_COLOURS, DateRangeFilterValue, FilterPill } from '@/utils/buildFilterPills';

const fallbackTableData: DocWithFacts[] = [];
const columnHelper = createColumnHelper<DocWithFacts>();

interface TableMeta {
  processingDocIds?: Set<string>;
  selectedCount?: number;
  totalCount?: number;
  isSelectingAll?: boolean;
  onSelectAllFiltered?: () => Promise<void>;
  onDeselectAll?: () => void;
}

const columns = [
  // Select checkbox - always first
  columnHelper.display({
    id: 'select',
    header: ({ table }) => {
      const meta = table.options.meta as TableMeta | undefined;
      const selectedCount = meta?.selectedCount ?? 0;
      const totalCount = meta?.totalCount ?? 0;
      const isSelectingAll = meta?.isSelectingAll ?? false;
      const onSelectAllFiltered = meta?.onSelectAllFiltered;
      const onDeselectAll = meta?.onDeselectAll;

      // Determine checkbox state
      const isAllSelected = selectedCount > 0 && selectedCount >= totalCount;

      const handleChange = () => {
        if (isAllSelected) {
          // Deselect all
          onDeselectAll?.();
        } else {
          // Select all filtered
          onSelectAllFiltered?.();
        }
      };

      if (isSelectingAll) {
        return (
          <div className="flex w-full items-center justify-center text-center">
            <Icon className="lucide--loader-circle h-4 w-4 animate-spin" />
          </div>
        );
      }

      return (
        <div className="flex w-full items-center justify-center text-center">
          <Checkbox checked={isAllSelected} onCheckedChange={handleChange} disabled={totalCount === 0} />
        </div>
      );
    },
    cell: ({ table, row }) => {
      const { processingDocIds } = (table.options.meta as TableMeta) ?? {};
      const isProcessing = processingDocIds?.has(row.original.id);

      if (isProcessing) {
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex w-full min-w-5 items-center justify-center text-center">
                <Icon className="lucide--loader-2 h-4 w-4 animate-spin text-amber-500 duration-1500" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>This document is currently being processed</p>
            </TooltipContent>
          </Tooltip>
        );
      }

      return (
        <div className="flex w-full min-w-5 items-center justify-center text-center">
          <Checkbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onCheckedChange={row.getToggleSelectedHandler()}
          />
        </div>
      );
    },
    filterFn: selectedRowsFilter,
  }),
  // Filename - always shown
  columnHelper.accessor('file.name', {
    id: 'file.name',
    header: () => <span>Filename</span>,
    cell: (info) => {
      const doc = info.row.original;
      const { openReferenceDrawer } = useStore();

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className="line-clamp-1 w-fit break-all text-blue-600 hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                openReferenceDrawer({ docId: doc.id });
              }}
            >
              {info.getValue()}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>{info.getValue()}</p>
          </TooltipContent>
        </Tooltip>
      );
    },
    sortingFn: sortStringWithEmptyLast,
    meta: {
      sortIcon: 'string',
    },
  }),
  // Checked by default columns
  columnHelper.accessor('type', {
    header: () => <span>Type</span>,
    cell: (info) =>
      info.getValue() ? (
        <Chip className={cn(CHIP_COLOURS.type)}>
          <div className="line-clamp-1 max-w-28 text-ellipsis">{info.getValue()}</div>
        </Chip>
      ) : (
        <TableNullField />
      ),
    filterFn: includedInArr,
    enableHiding: true,
    enableSorting: false,
  }),
  columnHelper.accessor('file.extension', {
    id: 'file.extension',
    header: () => <span>Extension</span>,
    cell: (info) => {
      const value = info.getValue();
      return value ? (
        <Chip className={cn(CHIP_COLOURS.extension)}>
          <div className="line-clamp-1 max-w-28 text-ellipsis">{value.toUpperCase()}</div>
        </Chip>
      ) : (
        <TableNullField />
      );
    },
    filterFn: includedInArr,
    enableHiding: true,
    enableSorting: false,
  }),
  columnHelper.accessor('issues', {
    header: () => <span>Issues</span>,
    cell: (info) => {
      const issues = info.getValue();

      if (!issues.length) return <TableNullField />;

      return (
        <Popover>
          <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button size="sm" className="gap-0 py-0.5">
              <span>
                {issues.length} <span className="font-normal">{issues.length === 1 ? 'issue' : 'issues'}</span>
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full max-w-min" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-wrap gap-2.5">
              {issues.map((issue) => (
                <Chip key={issue} className={cn(CHIP_COLOURS.issue)}>
                  {issue}
                </Chip>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      );
    },
    filterFn: arrInArr,
    enableSorting: false,
    enableHiding: true,
  }),
  columnHelper.accessor('persons', {
    id: 'persons',
    header: () => <span>Entities</span>,
    cell: (info) => {
      const persons = info.getValue() as { id: string; name: string }[] | null | undefined;

      if (!persons || persons.length === 0) return <TableNullField />;

      return (
        <Popover>
          <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button size="sm" className="gap-0 py-0.5">
              <span>
                {persons.length} <span className="font-normal">{persons.length === 1 ? 'entity' : 'entities'}</span>
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full max-w-min" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-wrap gap-2.5">
              {persons.map((person) => (
                <Chip key={person.id} className="border-teal-300 bg-teal-100 text-teal-800">
                  {person.name}
                </Chip>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      );
    },
    filterFn: (row, columnId, filterValue: string[]) => {
      if (!filterValue || filterValue.length === 0) return true;
      const persons = row.getValue(columnId) as { id: string; name: string }[] | null | undefined;
      if (!persons || persons.length === 0) return false;
      const personNames = persons.map((p) => p.name);
      return filterValue.some((name) => personNames.includes(name));
    },
    enableSorting: false,
    enableHiding: true,
  }),
  columnHelper.accessor('date', {
    header: () => <span>Document Date</span>,
    cell: (info) =>
      info.getValue() !== null ? (
        <span className="whitespace-nowrap">
          {new Date(info.getValue()!).toLocaleString('en-GB', {
            dateStyle: 'medium',
          })}
        </span>
      ) : (
        <TableNullField />
      ),
    enableHiding: true,
    filterFn: (row, columnId, filterValue: DateRangeFilterValue) => {
      const { startDate, endDate } = filterValue || {};

      if (!startDate && !endDate) return true;

      const docDate = row.getValue(columnId);
      if (!docDate) return false; // Exclude docs without dates when filter active

      const date = new Date(docDate as string);

      if (startDate && date < startDate) return false;

      if (endDate) {
        const endOfDay = new Date(endDate);
        endOfDay.setHours(23, 59, 59, 999);
        if (date > endOfDay) return false;
      }

      return true;
    },
  }),
  // Unchecked by default columns
  columnHelper.accessor('author', {
    header: () => <span>Author</span>,
    cell: (info) =>
      info.getValue() ? <div className="line-clamp-1 max-w-64 break-all">{info.getValue()}</div> : <TableNullField />,
    sortingFn: sortStringWithEmptyLast,
    enableHiding: true,
    meta: {
      sortIcon: 'string',
    },
  }),
  columnHelper.accessor('factsCount', {
    header: () => <span>No. of Facts</span>,
    enableHiding: true,
    meta: {
      sortIcon: 'number',
    },
  }),
  columnHelper.accessor('file.createdDate', {
    id: 'file.createdDate',
    header: () => <span>Run Date/Time</span>,
    cell: (info) =>
      info.getValue() !== null ? (
        <span className="whitespace-nowrap">
          {new Date(info.getValue()!).toLocaleString('en-GB', {
            dateStyle: 'medium',
            timeStyle: 'short',
          })}
        </span>
      ) : (
        <TableNullField />
      ),
    enableHiding: true,
    filterFn: (row, columnId, filterValue: string[]) => {
      if (!filterValue || filterValue.length === 0) return true;

      const uploadDate = row.getValue(columnId);
      if (!uploadDate) return false;

      // Format the row's date to match the filter format
      const formattedDate = new Date(uploadDate as string).toLocaleString('en-GB', {
        dateStyle: 'medium',
        timeStyle: 'short',
      });

      return filterValue.includes(formattedDate);
    },
    meta: {
      sortIcon: 'string',
    },
  }),
];

// Rename static columns for clarity - dynamic Relativity columns will be added at runtime
const staticColumns = columns;

// Cell renderer for dynamic Relativity fields based on field type
function renderRelativityCell(value: unknown, fieldType: string) {
  if (value === null || value === undefined) return <TableNullField />;

  switch (fieldType) {
    case 'string':
      return <span className="line-clamp-1 max-w-64 break-all">{String(value)}</span>;
    case 'number':
      return <span>{(value as number).toLocaleString()}</span>;
    case 'string[]': {
      if (!Array.isArray(value)) {
        return <span className="line-clamp-1 max-w-64 break-all">{String(value)}</span>;
      }

      const arr = value as string[];
      if (!arr.length) return <TableNullField />;
      return (
        <Popover>
          <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button size="sm" className="gap-0 bg-purple-50 py-0.5 text-purple-700 hover:bg-purple-100">
              <span>
                {arr.length} <span className="font-normal">{arr.length === 1 ? 'value' : 'values'}</span>
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full max-w-min" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-wrap gap-2.5">
              {arr.map((item, i) => (
                <Chip key={i} className="bg-purple-100/60 text-purple-700">
                  {item}
                </Chip>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      );
    }
    case 'number[]': {
      if (!Array.isArray(value)) {
        return <span className="line-clamp-1 max-w-64 break-all">{String(value)}</span>;
      }

      const numArr = value as number[];
      if (!numArr.length) return <TableNullField />;
      return <span>{numArr.join(', ')}</span>;
    }
    default:
      return <span className="line-clamp-1 max-w-64 break-all">{String(value)}</span>;
  }
}

// Custom filter function for Relativity fields - handles both scalar and array values
const relativityFilterFn: FilterFn<DocWithFacts> = (row, columnId, filterValue: string[]) => {
  if (!filterValue || filterValue.length === 0) return true;

  const cellValue = row.getValue(columnId);
  if (cellValue === null || cellValue === undefined) return false;

  // Handle array cell values (OR logic - match if any element is in filter)
  if (Array.isArray(cellValue)) {
    return cellValue.some((v) => filterValue.includes(String(v)));
  }

  // Handle scalar cell values
  return filterValue.includes(String(cellValue));
};

// Generate dynamic column definitions from Relativity field mappings
function generateRelativityColumns(fields: FilterField[]) {
  return fields.map((field) => {
    const columnId = `file.fileMetadata.relativityFields.${field.fieldName}`;

    return columnHelper.accessor((row) => row.file?.fileMetadata?.relativityFields?.[field.fieldName], {
      id: columnId,
      header: () => (
        <span className="flex items-center gap-1.5">
          <Icon className="lucide--database h-3 w-3 text-purple-600" />
          {field.displayName}
        </span>
      ),
      cell: (info) => renderRelativityCell(info.getValue(), field.fieldType),
      enableHiding: true,
      filterFn: relativityFilterFn,
      enableSorting: false,
    });
  });
}

// Generate dynamic column definitions from Wexler metadata fields
function generateWexlerColumns(fields: FilterField[]) {
  return fields.map((field) => {
    const columnId = `file.fileMetadata.wexlerFields.${field.fieldName}`;

    return columnHelper.accessor((row) => row.file?.fileMetadata?.wexlerFields?.[field.fieldName], {
      id: columnId,
      header: () => <span>{field.displayName}</span>,
      cell: (info) => renderRelativityCell(info.getValue(), field.fieldType),
      enableHiding: true,
      filterFn: relativityFilterFn,
      enableSorting: false,
    });
  });
}

interface DocPickerTableProps {
  docs: DocWithFacts[] | undefined;
  rowSelection: RowSelectionState;
  onRowSelectionChange: Dispatch<SetStateAction<RowSelectionState>>;
  allDocumentsCaseId: string;
  matterId: string;
  mode?: 'create' | 'edit';
  initialDocIds?: string[];
  isLoadingInitialSelection?: boolean;
  isLoadingDocs?: boolean;
  processingDocIds?: Set<string>;
  relativityFields?: FilterField[];
  relativityDistinctValues?: Record<string, string[]>;
  wexlerFields?: FilterField[];
  wexlerDistinctValues?: Record<string, string[]>;
  // Filter options from database (all possible values for the matter)
  docFilterOptions?: MatterDocFilterOptions;
  // Server-side pagination props
  pagination: PaginationState;
  onPaginationChange: OnChangeFn<PaginationState>;
  pageCount: number;
  totalCount: number;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  // Server-side filter props
  serverFilters: ServerFilters;
  onServerFiltersChange: (filters: ServerFilters) => void;
  // Search query props
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  // Show included toggle (controlled by parent for server-side filtering)
  showIncluded: boolean;
  onShowIncludedChange: (value: boolean) => void;
  // Select all handlers
  onSelectAllFiltered: () => Promise<void>;
  onDeselectAll: () => void;
  isSelectingAll?: boolean;
}

export function DocPickerTable({
  docs,
  rowSelection,
  onRowSelectionChange,
  allDocumentsCaseId,
  matterId,
  mode,
  initialDocIds,
  isLoadingInitialSelection,
  isLoadingDocs,
  processingDocIds,
  relativityFields,
  relativityDistinctValues,
  wexlerFields,
  wexlerDistinctValues,
  docFilterOptions,
  pagination,
  onPaginationChange,
  pageCount,
  totalCount,
  sorting,
  onSortingChange,
  serverFilters,
  onServerFiltersChange,
  searchQuery,
  onSearchQueryChange,
  showIncluded,
  onShowIncludedChange,
  onSelectAllFiltered,
  onDeselectAll,
  isSelectingAll,
}: DocPickerTableProps) {
  // Local search query state for input field (doesn't trigger search on each keystroke)
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const [isProcessingNL, setIsProcessingNL] = useState(false);
  const [isColumnConfigOpen, setIsColumnConfigOpen] = useState(false);

  // Sync local search query with prop when prop changes (e.g., when parent clears it)
  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  const docsWithFacts = (docs ?? []) as DocWithFacts[];

  // Column visibility configuration with localStorage persistence
  const { columnVisibility, setColumnVisibility } = useColumnConfiguration(matterId, relativityFields, wexlerFields);

  // Generate dynamic columns and merge with static columns
  const allColumns = useMemo(() => {
    const cols = [...staticColumns];
    if (wexlerFields?.length) {
      cols.push(...generateWexlerColumns(wexlerFields));
    }
    if (relativityFields?.length) {
      cols.push(...generateRelativityColumns(relativityFields));
    }
    return cols;
  }, [relativityFields, wexlerFields]);

  const selectedCount = useMemo(() => Object.keys(rowSelection).length, [rowSelection]);

  // Check if there are changes from initial selection (edit mode only)
  const hasSelectionChanges = useMemo(() => {
    if (mode !== 'edit' || !initialDocIds) return false;

    const currentIds = Object.keys(rowSelection);
    if (currentIds.length !== initialDocIds.length) return true;

    return !currentIds.every((id) => initialDocIds.includes(id));
  }, [mode, initialDocIds, rowSelection]);

  // Reset selection to initial state
  const handleResetSelection = useCallback(() => {
    if (!initialDocIds) return;

    const initialSelection = initialDocIds.reduce((acc, docId) => {
      acc[docId] = true;
      return acc;
    }, {} as RowSelectionState);

    onRowSelectionChange(initialSelection);
    trackCustomEvent(TrackingEventsV2.SubsetSelectorResetSelection);
  }, [initialDocIds, onRowSelectionChange]);

  const { buildFiltersWithNL, isProcessing: isProcessingNLFiltersQuery } = useDocumentNaturalLanguageFilters({
    caseId: allDocumentsCaseId,
    onFiltersChange: (newFilters: DocFilters) => {
      // Convert person names to person IDs
      const personIds = newFilters.personNames?.length
        ? newFilters.personNames
            .map((name) => docFilterOptions?.persons?.find((p) => p.name === name)?.id)
            .filter((id): id is string => !!id)
        : undefined;

      // Convert DocFilters to MatterDocsFilters for server-side filtering
      const serverFilterUpdate: MatterDocsFilters = {
        ...serverFilters,
        documentTypes: newFilters.documentTypes?.length ? newFilters.documentTypes : undefined,
        extensions: newFilters.extensions?.length ? newFilters.extensions : undefined,
        documentThemes: newFilters.documentThemes?.length ? newFilters.documentThemes : undefined,
        personIds: personIds?.length ? personIds : undefined,
        startDate: newFilters.startDate || undefined,
        endDate: newFilters.endDate || undefined,
        runIds: newFilters.runIds?.length ? newFilters.runIds : undefined,
      };

      onServerFiltersChange(serverFilterUpdate);

      // Track filter addition when filters are applied from NL search
      if (Object.values(serverFilterUpdate).some((v) => v !== undefined)) {
        trackCustomEvent(TrackingEventsV2.AddFilterToSubset);
      }
    },
    onSearchChange: (query: string) => {
      setLocalSearchQuery(query);
      onSearchQueryChange(query);
    },
  });

  const onSearchCall = (searchValue: string, useNaturalLanguage: boolean) => {
    if (useNaturalLanguage && searchValue.trim()) {
      setIsProcessingNL(true);
      buildFiltersWithNL(searchValue);
    } else {
      // Plain text search - update search query in parent (triggers API call)
      onSearchQueryChange(searchValue.trim());
    }
  };

  const clearSearch = () => {
    setLocalSearchQuery('');
    onSearchQueryChange('');
    setIsProcessingNL(false);
  };

  // Update processing state based on NL query
  useEffect(() => {
    if (!isProcessingNLFiltersQuery) {
      setIsProcessingNL(false);
    }
  }, [isProcessingNLFiltersQuery]);

  // Build filter pills from server filters
  const filterPills = useMemo(() => {
    const pills: FilterPill[] = [];

    // Create a map from person IDs to names for display
    const personsMap = new Map<string, string>();
    docFilterOptions?.persons?.forEach((person) => {
      personsMap.set(person.id, person.name);
    });
    const runsMap = new Map<string, Date>();
    docFilterOptions?.runs?.forEach((run) => {
      runsMap.set(run.id, run.createdDate);
    });
    if (serverFilters.documentTypes?.length) {
      serverFilters.documentTypes.forEach((type, idx) => {
        pills.push({
          id: `type-${idx}`,
          label: `Type: ${type}`,
          color: ATTRIBUTE_COLOURS.docType,
          onRemove: () => {
            const newTypes = serverFilters.documentTypes?.filter((t) => t !== type);
            onServerFiltersChange({ ...serverFilters, documentTypes: newTypes?.length ? newTypes : undefined });
          },
        });
      });
    }

    if (serverFilters.extensions?.length) {
      serverFilters.extensions.forEach((ext, idx) => {
        pills.push({
          id: `ext-${idx}`,
          label: `Extension: ${ext.toUpperCase()}`,
          color: ATTRIBUTE_COLOURS.extension,
          onRemove: () => {
            const newExtensions = serverFilters.extensions?.filter((e) => e !== ext);
            onServerFiltersChange({ ...serverFilters, extensions: newExtensions?.length ? newExtensions : undefined });
          },
        });
      });
    }

    if (serverFilters.documentThemes?.length) {
      serverFilters.documentThemes.forEach((issue, idx) => {
        pills.push({
          id: `issue-${idx}`,
          label: `Issue: ${issue}`,
          color: ATTRIBUTE_COLOURS.theme,
          onRemove: () => {
            const newIssues = serverFilters.documentThemes?.filter((i) => i !== issue);
            onServerFiltersChange({ ...serverFilters, documentThemes: newIssues?.length ? newIssues : undefined });
          },
        });
      });
    }

    if (serverFilters.personIds?.length) {
      serverFilters.personIds.forEach((personId, idx) => {
        const personName = personsMap.get(personId) || personId;
        pills.push({
          id: `person-${personId}-${idx}`,
          label: `Entity: ${personName}`,
          color: ATTRIBUTE_COLOURS.person,
          onRemove: () => {
            const newPersonIds = serverFilters.personIds?.filter((p) => p !== personId);
            onServerFiltersChange({ ...serverFilters, personIds: newPersonIds?.length ? newPersonIds : undefined });
          },
        });
      });
    }
    if (serverFilters.runIds?.length) {
      serverFilters.runIds.forEach((run, idx) => {
        const runCreatedDate = runsMap.get(run) || run;
        pills.push({
          id: `run-${idx}`,
          label: `Run: ${formatDate(runCreatedDate, true, true)}`,
          color: ATTRIBUTE_COLOURS.processed,
          onRemove: () => {
            const runIds = serverFilters.runIds?.filter((e) => e !== run);
            onServerFiltersChange({ ...serverFilters, runIds: runIds?.length ? runIds : undefined });
          },
        });
      });
    }
    if (serverFilters.startDate || serverFilters.endDate) {
      let dateLabel = 'Date: ';
      if (serverFilters.startDate && serverFilters.endDate) {
        dateLabel += `${new Date(serverFilters.startDate).toLocaleDateString()} - ${new Date(serverFilters.endDate).toLocaleDateString()}`;
      } else if (serverFilters.startDate) {
        dateLabel += `After ${new Date(serverFilters.startDate).toLocaleDateString()}`;
      } else if (serverFilters.endDate) {
        dateLabel += `Before ${new Date(serverFilters.endDate).toLocaleDateString()}`;
      }
      pills.push({
        id: 'date-range',
        label: dateLabel,
        color: ATTRIBUTE_COLOURS.date,
        onRemove: () => {
          onServerFiltersChange({ ...serverFilters, startDate: undefined, endDate: undefined });
        },
      });
    }

    // Add pills for metadata fields (both Wexler and Relativity)
    if (serverFilters.metadataFilters) {
      serverFilters.metadataFilters.forEach((filter) => {
        const fieldName = filter.field;
        const values = Array.isArray(filter.value) ? filter.value : [filter.value];

        // Find the field definition to get display name
        const wexlerField = wexlerFields?.find((f) => f.fieldName === fieldName);
        const relativityField = relativityFields?.find((f) => f.fieldName === fieldName);
        const field = wexlerField || relativityField;
        const displayName = field?.displayName || fieldName;
        const isRelativity = !!relativityField;

        values.forEach((value, idx) => {
          pills.push({
            id: `${isRelativity ? 'relativity' : 'wexler'}-${fieldName}-${idx}`,
            label: `${displayName}: ${value}`,
            color: isRelativity ? ATTRIBUTE_COLOURS.relativityMetadata : ATTRIBUTE_COLOURS.docType,
            icon: isRelativity ? 'lucide--database' : undefined,
            onRemove: () => {
              const newValues = values.filter((v) => v !== value);
              const currentMetadataFilters = serverFilters.metadataFilters ?? [];
              const newMetadataFilters =
                newValues.length > 0
                  ? currentMetadataFilters.map((f) =>
                      f.field === fieldName ? { field: fieldName, value: newValues as string[] } : f,
                    )
                  : currentMetadataFilters.filter((f) => f.field !== fieldName);
              onServerFiltersChange({
                ...serverFilters,
                metadataFilters: newMetadataFilters.length > 0 ? newMetadataFilters : undefined,
              });
            },
          });
        });
      });
    }

    return pills;
  }, [serverFilters, onServerFiltersChange, wexlerFields, relativityFields, docFilterOptions]);

  // Check if any server filters are active
  const hasActiveFilters = useMemo(() => {
    // Check search query
    const hasSearchQuery = !!searchQuery.trim();

    // Check standard filters
    const hasStandardFilters = !!(
      serverFilters.documentTypes?.length ||
      serverFilters.extensions?.length ||
      serverFilters.documentThemes?.length ||
      serverFilters.personIds?.length ||
      serverFilters.runIds?.length ||
      serverFilters.startDate ||
      serverFilters.endDate
    );

    // Check metadata filters
    const hasMetadataFilters = serverFilters.metadataFilters && serverFilters.metadataFilters.length > 0;

    return hasSearchQuery || hasStandardFilters || hasMetadataFilters;
  }, [serverFilters, searchQuery]);

  const table = useReactTable({
    data: docsWithFacts ?? fallbackTableData,
    columns: allColumns,
    state: {
      rowSelection,
      pagination,
      sorting,
      columnVisibility,
    },
    meta: {
      processingDocIds,
      selectedCount,
      totalCount,
      isSelectingAll,
      onSelectAllFiltered,
      onDeselectAll,
    } as TableMeta,
    // Server-side pagination configuration
    manualPagination: true,
    manualSorting: true,
    pageCount,
    // Row model configuration
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.id,
    enableRowSelection: (row) => !processingDocIds?.has(row.original.id),
    // Callbacks
    onRowSelectionChange: onRowSelectionChange,
    onPaginationChange: onPaginationChange,
    onSortingChange: onSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
  });

  return (
    <section className="flex grow flex-col overflow-hidden px-4" data-tour="doc-picker-table">
      <div className="my-4 flex flex-col gap-2">
        <div className="flex w-full items-center justify-end gap-2">
          <Toggle
            pressed={showIncluded}
            onPressedChange={(pressed) => {
              onShowIncludedChange(pressed);
              trackCustomEvent(TrackingEventsV2.SubsetSelectorShowSelectedDocuments);
            }}
            className="whitespace-nowrap"
            data-tour="show-selected-toggle"
          >
            <Icon className={showIncluded ? 'lucide--check-circle-2' : 'lucide--circle'} />
            <span>
              {showIncluded ? 'Showing' : 'Show'} {selectedCount} Included{' '}
              {selectedCount === 1 ? 'Document' : 'Documents'}
            </span>
          </Toggle>

          <Popover open={isColumnConfigOpen} onOpenChange={setIsColumnConfigOpen}>
            <PopoverTrigger asChild>
              <Button disabled={!docs}>
                <Icon className="lucide--columns-3" />
                <span>Edit Columns</span>
              </Button>
            </PopoverTrigger>
            <ColumnConfigPopover
              columnVisibility={columnVisibility}
              setColumnVisibility={setColumnVisibility}
              onClose={() => setIsColumnConfigOpen(false)}
              docs={docsWithFacts}
              relativityFields={relativityFields}
              wexlerFields={wexlerFields}
            />
          </Popover>

          {mode === 'edit' && (
            <Button
              onClick={handleResetSelection}
              disabled={!hasSelectionChanges}
              className="whitespace-nowrap duration-150"
            >
              <Icon className="lucide--rotate-ccw h-3.5 w-3.5" />
              <span>Reset Selection</span>
            </Button>
          )}
        </div>

        <div className="flex gap-2" data-tour="doc-filters">
          <div className="grow">
            <NaturalLanguageSearchBox
              value={localSearchQuery}
              onChange={setLocalSearchQuery}
              onSearchCall={onSearchCall}
              clearSearch={clearSearch}
              processingNL={isProcessingNL}
              entityType="docs-in-subset-creator"
            />
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <Button disabled={!docs}>
                <Icon className="lucide--funnel-plus" />
                <span>Add Filters</span>
              </Button>
            </PopoverTrigger>
            <FilterPopover
              serverFilters={serverFilters}
              onServerFiltersChange={onServerFiltersChange}
              filterOptions={docFilterOptions}
              relativityFields={relativityFields}
              relativityDistinctValues={relativityDistinctValues}
              wexlerFields={wexlerFields}
              wexlerDistinctValues={wexlerDistinctValues}
            />
          </Popover>

          <Button
            disabled={!hasActiveFilters}
            onClick={() => {
              onServerFiltersChange({});
              onSearchQueryChange('');
            }}
          >
            <Icon className="lucide--funnel-x" />
            <span>Clear Filters</span>
          </Button>
        </div>

        <FilterPills pills={filterPills} />
      </div>

      <div className="mt-2 grow overflow-auto">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-white">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    <div
                      className={cn(
                        'flex items-center gap-2 select-none',
                        header.column.getCanSort() && 'cursor-pointer',
                      )}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <SortIcon column={header.column} />
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {isLoadingInitialSelection || isLoadingDocs ? (
              Array.from({ length: pagination.pageSize }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {table.getVisibleLeafColumns().map((column) => (
                    <TableCell key={`skeleton-${i}-${column.id}`}>
                      <Skeleton className="h-5.5 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={table.getVisibleLeafColumns().length} className="h-64">
                  <Empty>
                    <EmptyHeader>
                      <EmptyMedia variant="icon">
                        <Icon className="lucide--file-x-2 h-5 w-5" />
                      </EmptyMedia>
                      <EmptyTitle>
                        {showIncluded && selectedCount === 0 ? 'No documents included' : 'No documents found'}
                      </EmptyTitle>
                      <EmptyDescription>
                        {showIncluded && selectedCount === 0
                          ? 'Select documents to include them in this subset'
                          : hasActiveFilters
                            ? 'Try adjusting your filters or search query'
                            : 'No documents available'}
                      </EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => {
                const isProcessing = processingDocIds?.has(row.original.id);
                return (
                  <TableRow
                    key={row.id}
                    onClick={() => !isProcessing && row.toggleSelected()}
                    className={cn(
                      'cursor-pointer',
                      isProcessing && 'cursor-not-allowed opacity-75',
                      !row.getIsSelected() && 'opacity-60',
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <TablePagination className="mb-4" table={table} plural="documents" totalCount={totalCount} />
    </section>
  );
}
